import React, {
	useState,
	useRef,
	useCallback,
	useMemo,
	createContext,
	useContext,
} from "react";
import {ReactIsland, type ControllerComponent} from "@/shared/reactIsland";
import type {WithCommandListener} from "@/shared/reactIsland";
import {CreateModalHandle, Modal} from "@/primitives/Modal";

/* Hook for closing the modal, exported */
type ModalContextValue = {close: () => void};
const ModalContext = createContext<ModalContextValue | null>(null);

export const ModalProvider = ModalContext.Provider;

export function useModal() {
	const ctx = useContext(ModalContext);
	if (!ctx) throw new Error("useModal must be used inside a Modal");
	return ctx;
}

type ModalCommands<InitialProps> = {
	Close: null;
	Open: Partial<InitialProps>;
};

interface ModalControllerProps<InitialProps extends object> {
	initialProps: InitialProps;
	component: ControllerComponent<InitialProps>;
}

export class ModalIsland<InitialProps extends object> {
	protected readonly island: ReactIsland<
		ModalControllerProps<InitialProps>,
		ModalCommands<InitialProps>
	>;

	private readonly modalControllerComponent: ControllerComponent<
		ModalControllerProps<InitialProps> &
		WithCommandListener<ModalCommands<InitialProps>>
	> = ({initialProps, component, addCommandListener}) => {
		/* Base UI handle drives the modal's open state imperatively. */
		const [handle] = useState(() => CreateModalHandle<Partial<InitialProps>>());
		const [openKey, setOpenKey] = useState(0);
		const payloadRef = useRef<Partial<InitialProps>>(initialProps);

		addCommandListener("Open", (payload: Partial<InitialProps>) => {
			payloadRef.current = payload;
			setOpenKey((k) => k + 1); // Trigger the reset
			handle.open(null);
		});
		addCommandListener("Close", () => handle.close());

		const close = useCallback(() => handle.close(), [handle]);
		const ctxValue = useMemo(() => ({close}), [close]);

		return (
			<ModalProvider value={ctxValue}>
				<Modal.Root handle={handle} disablePointerDismissal>
					{React.createElement(component, {
						key: openKey, // Component unmounts and remounts on every Open
						...initialProps,
						...payloadRef.current,
					})}
				</Modal.Root>
			</ModalProvider>
		);
	};

	constructor(
		component: ControllerComponent<InitialProps>,
		initialProps: InitialProps,
	) {
		this.island = new ReactIsland<
			ModalControllerProps<InitialProps>,
			ModalCommands<InitialProps>
		>(this.modalControllerComponent, {
			initialProps,
			component,
		});
	}

	public Open(payload: Partial<InitialProps> = {}) {
		this.island.dispatchCommand("Open", payload);
	}

	public Close() {
		this.island.dispatchCommand("Close", null);
	}
}
